import {
  ProjectSite,
  ProjectCriteria,
  ProjectReportAnalysisResponseDto,
} from '@/store/modules/projects/types/projects.types';
import { GenerateSingleSitePdf } from '@/components/reports/export/pdf/project/SingleSitePdf';
import { GenerateMultiSitePdf } from '@/components/reports/export/pdf/project/MultiSitePdf';
import { GenerateMultiSiteComparisonPdf } from '@/components/reports/export/pdf/project/MultiSiteComparisonPdf';
import { GenerateSingleSiteComparisonPdf } from '@/components/reports/export/pdf/project/SingleSiteComparisonPdf';
import * as pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import dayjs from 'dayjs';
import { ToastProgrammatic as Toast } from 'buefy';

export class ProjectReportExportToPdf {
  public async create(
    reportAnalysisResponse: ProjectReportAnalysisResponseDto[],
    projectTitle: string,
  ) {
    const pdfListArray: any[] = [];
    (pdfMake as any).vfs = pdfFonts.pdfMake.vfs;
    await Promise.all(
      reportAnalysisResponse.map(
        async (analysis: ProjectReportAnalysisResponseDto, index: number) => {
          pdfListArray[index] = await this.handleReportResponse(analysis);
        },
      ),
    );
    if (reportAnalysisResponse.length === 1) {
      const fileName =
        reportAnalysisResponse[0].analysisTitle +
        '_' +
        dayjs().format('DDMMMYYYY') +
        '.pdf';
      try {
        pdfMake.createPdf(pdfListArray[0] as any).download(fileName);
      } catch (err) {
        Toast.open({
          message: `Something went wrong. Try again.`,
          position: 'is-top',
          type: 'is-danger',
          duration: 3500,
        });
      } finally {
        Toast.open({
          message: `Export complete.`,
          position: 'is-top',
          type: 'is-dark',
          duration: 3500,
        });
      }
    } else {
      const fileName =
        projectTitle + '_' + dayjs().format('DDMMMYYYY') + '.pdf';
      const resultArray = pdfListArray[0];
      if (resultArray.content === undefined) {
        resultArray.content = [];
      }
      pdfListArray.forEach((pdfData, index) => {
        if (index !== 0 && pdfData.content && pdfData.content.length > 0) {
          const pdfContentArray = pdfData.content;
          pdfContentArray[0].pageBreak = 'before';
          resultArray.content.push(pdfContentArray);
        }
      });
      try {
        pdfMake.createPdf(resultArray as any).download(fileName);
      } catch (err) {
        Toast.open({
          message: `Something went wrong. Try again.`,
          position: 'is-top',
          type: 'is-danger',
          duration: 3500,
        });
      } finally {
        Toast.open({
          message: `Export complete.`,
          position: 'is-top',
          type: 'is-dark',
          duration: 3500,
        });
      }
    }
  }

  public async handleReportResponse(
    analysisResponse: ProjectReportAnalysisResponseDto,
  ) {
    const {
      result,
      payload,
      analysisTitle,
      jbiLogo,
      projectTitle,
      projectSites,
      projectCriteria,
    } = analysisResponse;
    switch (payload.comparisonPeriods.length) {
      case 1:
        return await this.generateSinglePeriodPdf(
          result,
          payload,
          analysisTitle,
          jbiLogo,
          projectTitle,
          projectSites,
          projectCriteria,
        );
      default:
        return await this.generateMultiPeriodPdf(
          result,
          payload,
          analysisTitle,
          jbiLogo,
          projectTitle,
          projectSites,
          projectCriteria,
        );
    }
  }

  public async generateSinglePeriodPdf(
    result: any,
    payload: any,
    analysisTitle: string,
    jbiLogo: string,
    projectTitle: string,
    projectSites: ProjectSite[],
    projectCriteria: ProjectCriteria[],
  ) {
    switch (projectSites.length) {
      case 1:
        const generateSingleSitePdf = new GenerateSingleSitePdf();
        return generateSingleSitePdf.create(
          result,
          payload,
          analysisTitle,
          jbiLogo,
          projectTitle,
          projectSites,
          projectCriteria,
        );
      default:
        const generateMultiSitePdf = new GenerateMultiSitePdf();
        return generateMultiSitePdf.create(
          result,
          payload,
          analysisTitle,
          jbiLogo,
          projectTitle,
          projectSites,
          projectCriteria,
        );
    }
  }

  public async generateMultiPeriodPdf(
    result: any,
    payload: any,
    analysisTitle: string,
    jbiLogo: string,
    projectTitle: string,
    projectSites: ProjectSite[],
    projectCriteria: ProjectCriteria[],
  ) {
    switch (projectSites.length) {
      case 1:
        const generateSingleSiteComparisonPdf = new GenerateSingleSiteComparisonPdf();
        return generateSingleSiteComparisonPdf.create(
          result,
          payload,
          analysisTitle,
          jbiLogo,
          projectTitle,
          projectSites,
          projectCriteria,
        );
      default:
        const generateMultiSiteComparisonPdf = new GenerateMultiSiteComparisonPdf();
        return generateMultiSiteComparisonPdf.create(
          result,
          payload,
          analysisTitle,
          jbiLogo,
          projectTitle,
          projectSites,
          projectCriteria,
        );
    }
  }
}
